import { Typography, Link } from '@mui/material';
import { DetailSection, DetailField } from './DetailSection';
import { CK } from '../portalStyles';

const fmt = (t) => {
  if (!t) return '';
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// GuardDuty (native finding) body, laid out the AWS-console way: grouped sections
// (Overview / Resource affected / Action / Actor / Location / Organization /
// Remediation). The rich fields come from the raw GuardDuty payload; each extra
// section renders only when its data is present.
export default function GuardDutyDetailBody({ finding }) {
  if (!finding) return null;
  const remediation = finding.remediation || {};

  let raw = {};
  try { raw = JSON.parse(finding.raw || '{}'); } catch (_) { raw = {}; }
  const svc = raw.Service || {};
  const action = svc.Action || {};
  const api = action.AwsApiCallAction || {};
  const net = action.NetworkConnectionAction || {};
  const remote = api.RemoteIpDetails || net.RemoteIpDetails || {};
  const city = remote.City || {};
  const country = remote.Country || {};
  const geo = remote.GeoLocation || {};
  const org = remote.Organization || {};
  const accessKey = (raw.Resource || {}).AccessKeyDetails || {};

  const hasAction = !!(action.ActionType || api.Api || api.ServiceName);
  const hasActor = !!remote.IpAddressV4;
  const hasLocation = !!(city.CityName || country.CountryName || geo.Lat != null || geo.Lon != null);
  const hasOrg = !!(org.Asn || org.AsnOrg || org.Isp || org.Org);
  const hasAccessKey = !!(accessKey.AccessKeyId || accessKey.PrincipalId || accessKey.UserName);

  return (
    <>
      <DetailSection title="Overview">
        <Typography variant="body2" sx={{ mb: 1.5, color: CK.navy, wordBreak: 'break-word' }}>
          {finding.description}
        </Typography>
        <DetailField label="Finding ID" value={finding.findingId || raw.Id} />
        <DetailField label="Finding type" value={finding.type} />
        <DetailField label="Account" value={finding.accountId} />
        <DetailField label="Region" value={finding.region} />
        {svc.Count != null && <DetailField label="Count" value={String(svc.Count)} />}
        <DetailField label="First observed" value={fmt(finding.firstObservedAt)} />
        <DetailField label="Last observed" value={fmt(finding.lastObservedAt)} />
      </DetailSection>

      <DetailSection title="Resource affected">
        {svc.ResourceRole && <DetailField label="Resource role" value={svc.ResourceRole} />}
        <DetailField label="Resource type" value={finding.resourceType} />
        <DetailField label="Resource" value={finding.resource || finding.resourceId} />
        {hasAccessKey && <DetailField label="Access key ID" value={accessKey.AccessKeyId} />}
        {hasAccessKey && <DetailField label="Principal ID" value={accessKey.PrincipalId} />}
        {hasAccessKey && <DetailField label="User type" value={accessKey.UserType} />}
        {hasAccessKey && <DetailField label="User name" value={accessKey.UserName} />}
      </DetailSection>

      {hasAction && (
        <DetailSection title="Action">
          <DetailField label="Action type" value={action.ActionType} />
          <DetailField label="API" value={api.Api} />
          <DetailField label="Service name" value={api.ServiceName} />
          <DetailField label="First seen" value={fmt(svc.EventFirstSeen)} />
          <DetailField label="Last seen" value={fmt(svc.EventLastSeen)} />
        </DetailSection>
      )}

      {hasActor && (
        <DetailSection title="Actor">
          <DetailField label="Caller type" value="Remote IP" />
          <DetailField label="IP address" value={remote.IpAddressV4} />
        </DetailSection>
      )}

      {hasLocation && (
        <DetailSection title="Location">
          <DetailField label="City" value={city.CityName} />
          <DetailField label="Country" value={country.CountryName} />
          {geo.Lat != null && <DetailField label="Lat" value={String(geo.Lat)} />}
          {geo.Lon != null && <DetailField label="Lon" value={String(geo.Lon)} />}
        </DetailSection>
      )}

      {hasOrg && (
        <DetailSection title="Organization">
          <DetailField label="ASN" value={org.Asn != null ? String(org.Asn) : ''} />
          <DetailField label="ASN org" value={org.AsnOrg} />
          <DetailField label="ISP" value={org.Isp} />
          <DetailField label="Org" value={org.Org} />
        </DetailSection>
      )}

      {(remediation.text || remediation.url) && (
        <DetailSection title="Remediation">
          {remediation.text && (
            <Typography variant="body2" sx={{ mb: 0.5, color: CK.navy, wordBreak: 'break-word' }}>
              {remediation.text}
            </Typography>
          )}
          {remediation.url && (
            <Link href={remediation.url} target="_blank" rel="noreferrer">View documentation</Link>
          )}
        </DetailSection>
      )}
    </>
  );
}
